import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";
import { createSlice } from "@reduxjs/toolkit";
import { CSRF_HEADER_NAME, getCachedCsrfToken } from "../api/csrf";

export const SOCKET_SCOPES = Object.freeze({
    ACCOUNT: 'account',
    PUBLIC: 'public'
});

export const socketSlice = createSlice({
    name: 'ws',
    initialState: {
        clients: {},
        connected: {},
        scopes: {}
    },
    reducers: {
        addSocket: (state, action) => {
            state.clients = { ...state.clients, ...{ [action.payload.clientName]: action.payload.client } };
            state.scopes = {
                ...state.scopes,
                [action.payload.clientName]: action.payload.scope || SOCKET_SCOPES.ACCOUNT
            };
        },
        setConnected: (state, action) => {
            if (!state.clients[action.payload.clientName]) {
                return;
            }
            state.connected = { ...state.connected, ...{ [action.payload.clientName]: action.payload.connected } };
        },
        removeSockets: (state, action) => {
            action.payload.forEach((clientName) => {
                delete state.clients[clientName];
                delete state.connected[clientName];
                delete state.scopes[clientName];
            });
        }
    }
})

export const { addSocket, setConnected, removeSockets } = socketSlice.actions;

export const createSocketConnectHeaders = ({
    scope,
    token,
    overlayToken,
    csrfToken
}) => scope === SOCKET_SCOPES.ACCOUNT
    ? (csrfToken ? { [CSRF_HEADER_NAME]: csrfToken } : {})
    : { token, overlay_token: overlayToken };

export const connectSocket = (
    clientName,
    url,
    topics,
    token = "",
    overlayToken = "",
    scope = SOCKET_SCOPES.ACCOUNT
) => (dispatch) => {
    const stompClient = new Client({
        connectHeaders: createSocketConnectHeaders({
            scope,
            token,
            overlayToken,
            csrfToken: getCachedCsrfToken()
        }),
        reconnectDelay: 30000,
        heartbeatIncoming: 10000,
        heartbeatOutgoing: 10000,
    });
    if (scope === SOCKET_SCOPES.ACCOUNT) {
        stompClient.beforeConnect = () => {
            stompClient.connectHeaders = createSocketConnectHeaders({
                scope,
                token,
                overlayToken,
                csrfToken: getCachedCsrfToken()
            });
        };
    }
    stompClient.webSocketFactory = function () {
        return new SockJS(url, {}, { transports: 'websocket' });
    };
    stompClient.onStompError = (error) => {
        console.log(error);
    }
    stompClient.onWebSocketError = (error) => {
        console.log(error);
    }
    stompClient.onConnect = function (frame) {
        console.log('Connected: ' + frame);
        dispatch(setConnected({ clientName: clientName, connected: true }))
        topics.forEach(topic => {
            stompClient.subscribe(topic.path, (message) => {
                topic.callback(message);
            });
        })
    }
    stompClient.onDisconnect = function (frame) {
        console.log('Disconnected: ' + frame);
        dispatch(setConnected({ clientName: clientName, connected: false }))
    }
    dispatch(addSocket({ clientName: clientName, client: stompClient, scope }));
}

export const disconnectSockets = (scope = SOCKET_SCOPES.ACCOUNT) => async (dispatch, getState) => {
    const socketState = getState().ws;
    const clientNames = Object.keys(socketState.clients)
        .filter((clientName) => socketState.scopes[clientName] === scope);

    await Promise.allSettled(clientNames.map((clientName) => {
        const client = socketState.clients[clientName];
        return client && typeof client.deactivate === 'function'
            ? client.deactivate()
            : Promise.resolve();
    }));

    dispatch(removeSockets(clientNames));
};

export default socketSlice.reducer;
